import { ChangeDetectorRef, ComponentFactoryResolver, ViewContainerRef } from '@angular/core';
import { AbcCore, CompareHelper } from '@barthauer/abc/core';
import {
  CoreDataEntityHelper,
  NetworkTraceModel,
  NetworkTraceObject,
  Query,
  QueryColumn,
  QueryObjectsModel,
  QueryOutputMode,
} from '@barthauer/abc/core/data';
import { forkJoin, Observable, of } from 'rxjs';
import { concatMap, map } from 'rxjs/operators';
import { DataEntity, MapService, ObjectTypesHelper } from '..';
import { PathSelectorComponent } from './controls/path-selector/path-selector.component';

class NetworkTraceObjectWithNode {
  PipeId: string;
  StartNodeId: string;
  StartNodeObjectType: string;
  EndNodeId: string;
  EndNodeObjectType: string;
  SubNodeId: string;
  SubNodeObjectType: string;
  constructor(data: any) {
    Object.assign(this, data);
  }
}

export class MapSelectionPath {
  private pathSelector: PathSelectorComponent;
  private pathEntities: NetworkTraceObject[];
  private pathEntitiesWithNodes: NetworkTraceObject[];
  private includeNodes: boolean;
  private displayDelay: number = 350;

  constructor(
    private componentResolver: ComponentFactoryResolver,
    private viewContainerRef: ViewContainerRef,
    private changeDetectorRef: ChangeDetectorRef,
    private abcCore: AbcCore,
    private mapService: MapService,
    public readonly startEntity: DataEntity,
    private onClosed: () => void
  ) {}

  public selectPathEntities(includeNodes: boolean): void {
    this.pathEntities = [this.createNetworkTraceObject(this.startEntity)];
    this.pathEntitiesWithNodes = null;
    this.includeNodes = includeNodes;

    if (!this.pathSelector) {
      this.pathSelector = this.loadPathSelector();
      this.pathSelector.pathSelectionClosed.subscribe(() => this.onPathSelectorClosed(includeNodes));
      this.pathSelector.cancelPathSelection.subscribe(() => this.onCancelPathSelection());
    }

    this.showPathSelector();
  }

  public nextEntitySelected(entity: DataEntity): void {
    if (entity) {
      this.findShortestPathEntities(this.createNetworkTraceObject(entity), this.includeNodes)
        .pipe(
          concatMap((_) => this.getPathEntities(this.includeNodes)),
          map((entities) => entities.map((_) => this.createNetworkTraceObject(_)))
        )
        .subscribe((entities) => {
          if (this.includeNodes) {
            this.pathEntitiesWithNodes = entities;
          }
          this.displayEntitiesWithDelay(entities);
        });
    } else {
      this.displayEntitiesWithDelay(this.pathEntitiesWithNodes ?? this.pathEntities);
    }
  }

  private createNetworkTraceObject(entity: DataEntity): NetworkTraceObject {
    return new NetworkTraceObject({
      Id: entity.id,
      ObjectTypeName: entity.objectType,
    });
  }

  private loadPathSelector(): PathSelectorComponent {
    const fcFactory = this.componentResolver.resolveComponentFactory(PathSelectorComponent);
    const viewContainerRef = this.viewContainerRef;

    const pathSelectorComponent = viewContainerRef.createComponent(fcFactory);
    this.changeDetectorRef.detectChanges();

    return <PathSelectorComponent>pathSelectorComponent.instance;
  }

  private showPathSelector() {
    this.pathSelector?.setVisible(true);
  }

  private onCancelPathSelection() {
    this.mapService?.navigateToObjects([]);
    if (this.onClosed) {
      this.onClosed();
    }
  }

  private onPathSelectorClosed(includeNodes: boolean) {
    this.getPathEntities(includeNodes).subscribe((entities) => this.mapService.raiseNetworkPathSelected(entities));
    if (this.onClosed) {
      this.onClosed();
    }
  }

  private findShortestPathEntities(
    nextEntity: NetworkTraceObject,
    includeNodes: boolean
  ): Observable<NetworkTraceObject[]> {
    const lastEntity = this.pathEntities[this.pathEntities.length - 1];
    return this.createNetworkTraceModel(lastEntity, nextEntity).pipe(
      concatMap((networkQuery) =>
        this.mapService.queryShortestPath(networkQuery).pipe(
          map((results) => {
            if (results?.length > 1) {
              let isFirst = true;
              for (const r of results) {
                if (
                  (!includeNodes || (isFirst && !lastEntity.FirstNode && !lastEntity.SecondNode)) &&
                  lastEntity.Id === r.Id
                ) {
                  this.pathEntities[this.pathEntities.length - 1] = r;
                } else {
                  this.pathEntities.push(r);
                }
                isFirst = false;
              }
              if (this.pathEntities[this.pathEntities.length - 1].Id !== nextEntity.Id) {
                this.pathEntities.push(nextEntity);
              }
            } else if (
              results?.length &&
              (!results.find(
                (_) => _.Id !== nextEntity.Id && _.FirstNode?.id !== nextEntity.Id && _.SecondNode?.id !== nextEntity.Id
              ) ||
                (results.length === 1 && lastEntity.Id !== results[0].Id))
            ) {
              if (
                (!includeNodes || (!lastEntity.FirstNode && !lastEntity.SecondNode)) &&
                lastEntity.Id === nextEntity.Id
              ) {
                this.pathEntities[this.pathEntities.length - 1] = results[0];
              } else {
                this.pathEntities.push(results[0]);
              }
            }
            return this.pathEntities;
          })
        )
      )
    );
  }

  private getEdgeEntities(
    pathEntities: NetworkTraceObject[]
  ): Observable<{ mainTypeName: string; entities: NetworkTraceObject[] }> {
    return of(pathEntities).pipe(
      concatMap((entities) => {
        const observables: Observable<string>[] = [];
        for (const e of entities) {
          observables.push(this.getMainTypeName(e));
        }
        return forkJoin(observables);
      }),
      map((mainTypeNames) => {
        const edgeEntities: NetworkTraceObject[] = [];
        let edgeMainTypeName: string;
        for (let i = 0; i < mainTypeNames.length; i++) {
          if (!ObjectTypesHelper.isNodeObjectType(mainTypeNames[i])) {
            edgeMainTypeName = mainTypeNames[i];
            edgeEntities.push(pathEntities[i]);
          }
        }
        return { mainTypeName: edgeMainTypeName, entities: edgeEntities };
      })
    );
  }

  private getPathEntities(includeNodes: boolean): Observable<DataEntity[]> {
    return this.getEdgeEntities(this.pathEntities).pipe(
      concatMap((result) => {
        if (includeNodes && !result.entities.find((_) => !_.FirstNode && !_.SecondNode)) {
          const entities: DataEntity[] = [];
          for (const edge of result.entities) {
            if (entities.length === 0 && edge.FirstNode) {
              entities.push(edge.FirstNode);
            }
            entities.push(new DataEntity(edge.ObjectTypeName, edge.Id, null, edge.IsInFlowDirection));
            if (edge.SecondNode) {
              entities.push(edge.SecondNode);
            }
          }
          return of(entities);
        } else if (!includeNodes || result.entities.length === 0) {
          return of(result.entities.map((_) => new DataEntity(_.ObjectTypeName, _.Id, null, _.IsInFlowDirection)));
        } else {
          return this.queryPathNodeEntitiesCore(result.mainTypeName, result.entities).pipe(
            map((pipeWithNodes) => {
              const allEntities: DataEntity[] = [];

              if (pipeWithNodes && pipeWithNodes.length > 0) {
                for (const traceedge of result.entities) {
                  const edge = new DataEntity(
                    traceedge.ObjectTypeName,
                    traceedge.Id,
                    null,
                    traceedge.IsInFlowDirection
                  );
                  const pipeWithNode = pipeWithNodes.find((pipe) => pipe.PipeId === edge.id);

                  let edgeWasAdded = false;

                  if (pipeWithNode) {
                    let startNodeEntity: DataEntity;
                    let endNodeEntity: DataEntity;
                    const startNodeId: string = traceedge['StartId'];
                    let startNodeObjectType: string;
                    if (startNodeId) {
                      let pipeWithNodeStart = pipeWithNodes.find(
                        (pipe) => pipe.PipeId === edge.id && pipe.SubNodeId === startNodeId
                      );
                      startNodeObjectType = pipeWithNodeStart?.SubNodeObjectType;
                    }
                    const endNodeId: string = traceedge['EndId'];
                    let endNodeObjectType: string;
                    if (endNodeId) {
                      let pipeWithNodeEnd = pipeWithNodes.find(
                        (pipe) => pipe.PipeId === edge.id && pipe.SubNodeId === endNodeId
                      );
                      endNodeObjectType = pipeWithNodeEnd?.SubNodeObjectType;
                    }
                    if (pipeWithNode.StartNodeId && pipeWithNode.StartNodeObjectType)
                      startNodeEntity = new DataEntity(
                        startNodeObjectType ?? pipeWithNode.StartNodeObjectType,
                        startNodeId ?? pipeWithNode.StartNodeId,
                        edge.isInFlowDirection,
                        undefined
                      );

                    if (pipeWithNode.EndNodeId && pipeWithNode.EndNodeObjectType)
                      endNodeEntity = new DataEntity(
                        endNodeObjectType ?? pipeWithNode.EndNodeObjectType,
                        endNodeId ?? pipeWithNode.EndNodeId,
                        edge.isInFlowDirection,
                        undefined
                      );

                    const beforeNode = edge.isInFlowDirection ?? true ? startNodeEntity : endNodeEntity;
                    if (
                      beforeNode &&
                      (allEntities.length === 0 ||
                        !CoreDataEntityHelper.equals(allEntities[allEntities.length - 1], beforeNode))
                    ) {
                      allEntities.push(beforeNode);
                    }

                    allEntities.push(edge);
                    edgeWasAdded = true;

                    const afterNode = edge.isInFlowDirection ?? true ? endNodeEntity : startNodeEntity;
                    if (
                      afterNode &&
                      (allEntities.length === 0 ||
                        !CoreDataEntityHelper.equals(allEntities[allEntities.length - 1], afterNode))
                    ) {
                      allEntities.push(afterNode);
                    }
                  }

                  if (!edgeWasAdded) {
                    allEntities.push(edge);
                  }
                }
              } else {
                allEntities.push(
                  ...result.entities.map((_) => new DataEntity(_.ObjectTypeName, _.Id, _.IsInFlowDirection))
                );
              }
              return allEntities;
            })
          );
        }
      })
    );
  }

  private queryPathNodeEntitiesCore(
    edgeMainTypeName: string,
    edgeEntities: NetworkTraceObject[]
  ): Observable<NetworkTraceObjectWithNode[]> {
    return this.queryPathNodes(edgeMainTypeName, edgeEntities).pipe(
      map((data) => (!CompareHelper.isEmptyObject(data) ? (data as any as object[]) : [])),
      map((data) => data.map((obj) => new NetworkTraceObjectWithNode(obj)))
    );
  }

  private queryPathNodes(edgeMainTypeName: string, edgeEntities: NetworkTraceObject[]): Observable<ArrayBuffer> {
    return this.createPathQueryForNodes(edgeMainTypeName, edgeEntities).pipe(
      concatMap((query) => this.abcCore.data.executeReadObjectsQuery(query))
    );
  }

  private createPathQueryForNodes(
    edgeMainTypeName: string,
    edgeEntities: NetworkTraceObject[]
  ): Observable<QueryObjectsModel> {
    const queryDocument: QueryObjectsModel = new QueryObjectsModel();
    const query: Query = new Query();
    if (edgeEntities.length) {
      query.ObjectType = edgeMainTypeName;
      const startNodeOpath: string = ObjectTypesHelper.getStartNodeOpathByEdgeType(edgeMainTypeName) + '.Id';
      const endNodeOpath: string = ObjectTypesHelper.getEndNodeOpathByEdgeType(edgeMainTypeName) + '.Id';
      const startNodeObjectTypeOpath: string =
        ObjectTypesHelper.getStartNodeOpathByEdgeType(edgeMainTypeName) + '.ObjectType';
      const endNodeObjectTypeOpath: string =
        ObjectTypesHelper.getEndNodeOpathByEdgeType(edgeMainTypeName) + '.ObjectType';
      const oPath: string = this.buildOPathFromEdgeEntities(edgeEntities);
      query.OPath = oPath;

      const colums: QueryColumn[] = [];
      colums.push({ Name: 'PipeId', OPath: 'Id' });
      colums.push({ Name: 'StartNodeId', OPath: startNodeOpath });
      colums.push({
        Name: 'StartNodeObjectType',
        OPath: startNodeObjectTypeOpath,
      });
      colums.push({ Name: 'EndNodeId', OPath: endNodeOpath });
      colums.push({
        Name: 'EndNodeObjectType',
        OPath: endNodeObjectTypeOpath,
      });
      query.Columns = colums;

      const subNodesOpath: string = ObjectTypesHelper.getSubNodeOpathByEdgeType(edgeMainTypeName);
      if (subNodesOpath) {
        const subquery: Query = new Query();
        subquery.OPath = subNodesOpath;
        subquery.DefaultIfEmpty = true;

        const subcolumns: QueryColumn[] = [];
        subcolumns.push({ Name: 'SubNodeId', OPath: 'Id' });
        subcolumns.push({ Name: 'SubNodeObjectType', OPath: 'ObjectType' });
        subquery.Columns = subcolumns;

        query.ObjectQueries = [subquery];
      }

      queryDocument.OutputMode = QueryOutputMode.Json;
      queryDocument.ObjectQueries = [query];

      return of(queryDocument);
    } else {
      return of(null);
    }
  }

  private buildOPathFromEdgeEntities(edgeEntities: NetworkTraceObject[]): string {
    let ret: string = '';
    if (edgeEntities && edgeEntities.length > 0) {
      for (let edge of edgeEntities) {
        if (!ret) ret = "Id in ('" + edge.Id + "'";
        else ret += ",'" + edge.Id + "'";
      }
    }
    if (ret) ret += ')';

    return ret;
  }

  private createNetworkTraceModel(
    startEntity: NetworkTraceObject,
    nextEntity: NetworkTraceObject
  ): Observable<NetworkTraceModel> {
    return this.getMainTypeName(startEntity).pipe(
      concatMap((typeStart) =>
        this.getMainTypeName(nextEntity).pipe(
          map((typeEnd) => {
            const networkQuery = new NetworkTraceModel();
            networkQuery.EdgeObjectType = ObjectTypesHelper.getEdgeObjectType([typeStart, typeEnd]);
            if (ObjectTypesHelper.isNodeObjectType(typeStart)) {
              networkQuery.StartNodeIds = [startEntity.Id];
            } else {
              networkQuery.StartEdgeIds = [startEntity.Id];
            }
            if (ObjectTypesHelper.isNodeObjectType(typeEnd)) {
              networkQuery.EndNodeIds = [nextEntity.Id];
            } else {
              networkQuery.EndEdgeIds = [nextEntity.Id];
            }
            return networkQuery;
          })
        )
      )
    );
  }

  private getMainTypeName(entity: NetworkTraceObject): Observable<string> {
    return this.abcCore.objectSpace.getObjectType(entity.ObjectTypeName).pipe(map((ot) => ot.mainType.name));
  }

  private displayEntitiesWithDelay(entities: NetworkTraceObject[]) {
    setTimeout(
      () =>
        this.mapService.navigateToObjectsWithoutZoom(
          entities.map((_) => new DataEntity(_.ObjectTypeName, _.Id, _.IsInFlowDirection))
        ),
      this.displayDelay
    );
  }
}
